import { useEffect, useMemo, useRef, useState } from "react";
import { searchArtists } from "@/lib/afmApi";

// Campo de nombre con sugerencias desde la primera letra:
// 1) nombres de este lanzamiento (artistas, feat., compositores, productores),
// 2) nombres que ya usaste antes (guardados en este navegador),
// 3) artistas del directorio de Always Fresh / Spotify (desde 2 letras).
const KEY = "afm_contrib_names";
export const rememberNames = (names, key = KEY) => {
  try {
    const prev = JSON.parse(localStorage.getItem(key) || "[]");
    const next = [...new Set([...names.map((n) => String(n || "").trim()).filter(Boolean), ...prev])].slice(0, 300);
    localStorage.setItem(key, JSON.stringify(next));
  } catch { /* sin almacenamiento: solo se pierden las sugerencias */ }
};
export const remembered = (key = KEY) => { try { return JSON.parse(localStorage.getItem(key) || "[]"); } catch { return []; } };
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

export default function NameSuggest({ value, onChange, suggestions = [], placeholder, className, testId, storageKey = KEY, remote: useRemote = true, useMemory = true }) {
  const [open, setOpen] = useState(false);
  const [remote, setRemote] = useState([]);
  const [hi, setHi] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = String(value || "").trim();
    if (!useRemote || !open || q.length < 2) { setRemote([]); return undefined; }
    timer.current = setTimeout(() => {
      searchArtists(q, 5).then((r) => setRemote([...(r?.internal || []), ...(r?.spotify || [])].map((a) => a?.name).filter(Boolean))).catch(() => setRemote([]));
    }, 300);
    return () => clearTimeout(timer.current);
  }, [value, open, useRemote]);

  const options = useMemo(() => {
    const q = norm(String(value || "").trim());
    const all = [...new Set([...suggestions, ...(useMemory ? remembered(storageKey) : []), ...remote].map((n) => String(n || "").trim()).filter(Boolean))];
    const starts = all.filter((n) => norm(n).startsWith(q));
    const contains = all.filter((n) => !norm(n).startsWith(q) && norm(n).includes(q));
    return [...starts, ...contains].filter((n) => norm(n) !== q).slice(0, 8);
  }, [value, suggestions, remote, storageKey, useMemory]);

  const pick = (n) => { onChange(n); setOpen(false); };
  return (
    <div className="relative flex-1">
      <input data-testid={testId} value={value} placeholder={placeholder} className={`${className} w-full`} autoComplete="off"
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHi(0); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (!open || !options.length) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHi((h) => Math.min(h + 1, options.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi((h) => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" || e.key === "Tab") { if (String(value || "").trim()) { e.preventDefault(); pick(options[hi]); } }
          else if (e.key === "Escape") setOpen(false);
        }} />
      {open && options.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-20 mt-1 max-h-56 overflow-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg dark:border-white/10 dark:bg-[#1c1c1c]">
          {options.map((n, i) => (
            <li key={n}><button type="button" onMouseDown={(e) => { e.preventDefault(); pick(n); }}
              className={`block w-full px-3 py-1.5 text-left text-sm ${i === hi ? "bg-slate-100 dark:bg-white/10" : ""} text-slate-800 hover:bg-slate-100 dark:text-slate-100 dark:hover:bg-white/10`}>{n}</button></li>
          ))}
        </ul>
      )}
    </div>
  );
}
